import { useState } from 'react';
import { User, MessageSquare, CheckSquare, ChevronRight, ArrowLeft } from 'lucide-react';
import Modal from './Modal';
import { useOneOnOneRelationships, DAY_NAMES } from '../hooks/useOneOnOneRelationships';

export default function OneOnOnePickerModal({
  isOpen,
  onClose,
  onSelect,
  itemTitle = ''
}) {
  const { activeRelationships, loading } = useOneOnOneRelationships();
  const [selectedRelationship, setSelectedRelationship] = useState(null);
  const [step, setStep] = useState('person'); // 'person' or 'type'

  const handlePersonSelect = (relationship) => {
    setSelectedRelationship(relationship);
    setStep('type');
  };

  const handleTypeSelect = (type) => {
    onSelect({
      relationship: selectedRelationship,
      type // 'topic' or 'actionItem'
    });
    handleClose();
  };

  const handleBack = () => {
    setStep('person');
    setSelectedRelationship(null);
  };

  const handleClose = () => {
    setStep('person');
    setSelectedRelationship(null);
    onClose();
  };

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Add to 1:1">
        <div className="flex items-center justify-center py-8">
          <span className="text-text-tertiary text-sm">Loading...</span>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={step === 'person' ? 'Add to 1:1' : `Add to ${selectedRelationship?.personName}'s 1:1`}
    >
      {/* Item preview */}
      {itemTitle && (
        <div className="mb-4 p-3 bg-surface-elevated rounded-lg border border-border">
          <span className="text-[10px] text-text-tertiary block mb-1">Adding:</span>
          <span className="text-sm text-text-primary">{itemTitle}</span>
        </div>
      )}

      {/* Step 1: Select Person */}
      {step === 'person' && (
        <div className="space-y-2">
          {activeRelationships.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-sm text-text-tertiary">No 1:1 relationships found.</p>
              <p className="text-xs text-text-muted mt-1">Add someone in the 1:1s tab first.</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-text-tertiary mb-3">Select a person:</p>
              {activeRelationships.map((relationship) => (
                <button
                  key={relationship.id}
                  onClick={() => handlePersonSelect(relationship)}
                  className="w-full flex items-center justify-between p-3 bg-surface-card border border-border rounded-lg hover:bg-surface-hover hover:border-border-strong cursor-pointer transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-surface-hover">
                      <User size={16} className="text-text-secondary" />
                    </div>
                    <div className="text-left">
                      <span className="text-sm font-medium text-text-primary block">
                        {relationship.personName}
                      </span>
                      <span className="text-[10px] text-text-tertiary">
                        {DAY_NAMES[relationship.meetingDay]}s
                      </span>
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-text-tertiary" />
                </button>
              ))}
            </>
          )}
        </div>
      )}

      {/* Step 2: Select Type */}
      {step === 'type' && selectedRelationship && (
        <div className="space-y-2">
          <button
            onClick={handleBack}
            className="flex items-center gap-1 text-xs text-text-tertiary hover:text-text-secondary mb-3 bg-transparent border-none cursor-pointer transition-all"
          >
            <ArrowLeft size={14} />
            Back
          </button>

          <p className="text-xs text-text-tertiary mb-3">Add as:</p>

          <button
            onClick={() => handleTypeSelect('topic')}
            className="w-full flex items-center gap-3 p-4 bg-surface-card border border-border rounded-lg hover:bg-surface-hover hover:border-weekly/50 cursor-pointer transition-all"
          >
            <div className="p-2 rounded-lg bg-weekly-light">
              <MessageSquare size={18} className="text-weekly" />
            </div>
            <div className="text-left">
              <span className="text-sm font-medium text-text-primary block">
                Discussion Topic
              </span>
              <span className="text-[10px] text-text-tertiary">
                Add to topics for your next 1:1
              </span>
            </div>
          </button>

          <button
            onClick={() => handleTypeSelect('actionItem')}
            className="w-full flex items-center gap-3 p-4 bg-surface-card border border-border rounded-lg hover:bg-surface-hover hover:border-accent/50 cursor-pointer transition-all"
          >
            <div className="p-2 rounded-lg bg-accent-muted">
              <CheckSquare size={18} className="text-accent" />
            </div>
            <div className="text-left">
              <span className="text-sm font-medium text-text-primary block">
                Action Item
              </span>
              <span className="text-[10px] text-text-tertiary">
                Add as a task for follow-up
              </span>
            </div>
          </button>
        </div>
      )}
    </Modal>
  );
}
